/**
 * LTI embedded-mode helpers for feedbackUI.
 *
 * When the SPA is launched from an LMS (Canvas / Schoolbox / Moodle) it renders
 * inside an iframe on the LMS page. In that context the app's normal
 * "lost-session" recovery — `localStorage.clear()` + `window.location.href = '/'`
 * (see service.js / service/api.js 401 handling) and the top-level IdP redirect
 * in App.jsx — are hostile: they either blow away unrelated storage, navigate the
 * *iframe* to a route that immediately 401s again, or try to redirect the WP IdP
 * inside a frame the IdP refuses to render (X-Frame-Options). The LMS owns the
 * session lifecycle, so the only correct recovery is "relaunch from your LMS".
 *
 * This module centralises:
 *   - detecting embedded mode (a sticky `?lti=1` flag OR a real cross-origin frame),
 *   - forcing the JWT into localStorage while embedded (cookies are unreliable in
 *     3rd-party iframes with cookies disabled — SR-10 fallback chain),
 *   - a tiny pub/sub so service.js can signal "session expired" and App.jsx can
 *     render the relaunch state without a hard navigation.
 *
 * Pure module-level helpers (no React), mirroring utils/login.js style so it can be
 * imported from both App.jsx and the non-React service layer.
 */

const LTI_EMBED_FLAG_KEY = 'lti.embedded';

/**
 * Frame detection, extracted behind a tiny overridable seam.
 *
 * `window.top` is an OWN, NON-CONFIGURABLE accessor in modern jsdom (and real
 * browsers), so a unit test CANNOT `jest.spyOn(window, 'top', 'get')` or
 * `Object.defineProperty(window, 'top', …)` to fake a frame — both throw. Rather
 * than depend on jsdom internals, the real check lives in `defaultIsFramed()` and
 * the exported `isFramed` indirection can be overridden in tests via
 * `__setIsFramedForTests()`. Production behaviour is unchanged.
 */
const defaultIsFramed = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // Accessing window.top across origins throws → we are in a cross-origin frame.
    return true;
  }
};

let isFramedImpl = defaultIsFramed;

const isFramed = () => isFramedImpl();

/**
 * Test-only seam: override the frame check (and restore it) without touching the
 * non-configurable `window.top` getter. Call with no args to reset to the real
 * implementation.
 */
export const __setIsFramedForTests = (fn) => {
  isFramedImpl = typeof fn === 'function' ? fn : defaultIsFramed;
};

/**
 * Read the merged query params from both the search string and the HashRouter
 * hash (App.jsx merges them the same way; we duplicate the minimal logic here so
 * this module stays dependency-free and usable from the service layer).
 */
const readParam = (key) => {
  try {
    const search = new URLSearchParams(window.location.search);
    if (search.has(key)) return search.get(key);
    const hash = window.location.hash || '';
    const qIndex = hash.indexOf('?');
    if (qIndex !== -1) {
      const hashParams = new URLSearchParams(hash.slice(qIndex + 1));
      if (hashParams.has(key)) return hashParams.get(key);
    }
  } catch (e) {
    // ignore malformed URLs
  }
  return null;
};

/**
 * Persist a sticky embedded flag so embedded mode survives client-side
 * navigation after the launch params (`?lti=1`, `?lti_code=`) have been stripped
 * from the URL. Set once at launch; cleared on explicit logout.
 */
export const markEmbeddedSession = () => {
  try {
    sessionStorage.setItem(LTI_EMBED_FLAG_KEY, '1');
  } catch (e) {
    // sessionStorage can throw in locked-down webviews; embedded detection then
    // falls back to the live frame check, which is still correct for this tab.
  }
};

export const clearEmbeddedSession = () => {
  try {
    sessionStorage.removeItem(LTI_EMBED_FLAG_KEY);
  } catch (e) {
    // ignore
  }
};

const hasStickyEmbeddedFlag = () => {
  try {
    return sessionStorage.getItem(LTI_EMBED_FLAG_KEY) === '1';
  } catch (e) {
    return false;
  }
};

/**
 * The canonical "are we embedded?" check used across App.jsx and service.js.
 *
 * Embedded when ANY of:
 *   - the launch URL carried `?lti=1` (explicit, set by the backend launch redirect),
 *   - a sticky session flag was set at launch (survives param stripping / SPA nav),
 *   - the tab is rendered in a cross-origin frame.
 */
export const isEmbeddedMode = () =>
  readParam('lti') === '1' || hasStickyEmbeddedFlag() || isFramed();

/**
 * True when the current URL carries an `?lti_code=` to be exchanged. Lets App.jsx
 * branch cheaply before doing any work.
 */
export const getLtiCodeFromUrl = () => readParam('lti_code');

/**
 * The opaque launch-context token threaded from the backend launch redirect
 * (PLAN §7.1 step 10 / §8.10). The SPA must forward this onto `POST /submissions`
 * so the backend can stamp `lti_submission_context` for AGS passback targeting.
 * Stored in sessionStorage so it survives the param-stripping `replaceState` and
 * the client-side navigation to `/#/tasks/{id}/start` before the submission is
 * created.
 */
const LCTX_KEY = 'lti.lctx';

export const rememberLaunchContext = (lctx) => {
  if (!lctx) return;
  try {
    sessionStorage.setItem(LCTX_KEY, lctx);
  } catch (e) {
    // ignore
  }
};

export const getLaunchContext = () => {
  try {
    return sessionStorage.getItem(LCTX_KEY) || null;
  } catch (e) {
    return null;
  }
};

export const clearLaunchContext = () => {
  try {
    sessionStorage.removeItem(LCTX_KEY);
  } catch (e) {
    // ignore
  }
};

/**
 * Minimal pub/sub so the (non-React) service layer can tell the React tree that
 * the embedded session is gone, without importing React or doing a hard
 * navigation. App.jsx subscribes and flips into the "relaunch from your LMS"
 * state.
 */
let sessionExpired = false;
const listeners = new Set();

export const isEmbeddedSessionExpired = () => sessionExpired;

/**
 * Called by service.js / service/api.js on a 401 while embedded. Idempotent.
 * Does NOT touch localStorage or navigate — that is the whole point.
 */
export const notifyEmbeddedSessionExpired = () => {
  if (sessionExpired) return;
  sessionExpired = true;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      // a bad listener must not break the others
    }
  });
};

export const onEmbeddedSessionExpired = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/**
 * Test-only reset so the module-level flag does not leak between Jest cases.
 */
export const __resetEmbeddedSessionForTests = () => {
  sessionExpired = false;
  listeners.clear();
  isFramedImpl = defaultIsFramed;
};
